import { useFrame, useThree } from "@react-three/fiber";
import { useContext, useEffect, useLayoutEffect, useMemo, useRef } from "react";
import {
  Color,
  type IUniform,
  type Material,
  Matrix4,
  Mesh,
  MeshDepthMaterial,
  type Object3D,
  OrthographicCamera,
  PlaneGeometry,
  Scene,
  ShaderMaterial,
  type Texture,
  Vector2,
  Vector3,
  WebGLRenderTarget,
} from "three";
import { useEnvironmentStudio } from "../stores/environmentStudioStore";
import { ProductStoreContext } from "./ProductStoreContext";
import { AO_EXCLUDED_LAYER } from "./ProductViewerPostProcessing";
import { isSceneDragActive } from "./productDragScope";
import { studioKeyDirection, studioYaw } from "./StandardEnvironment";
import { getSceneRevision } from "./sceneActivity";
import {
  isShadowProxy,
  SHADOW_PROXY_LAYER,
  shouldUseShadowProxy,
} from "./shadowCasterProxy";
import { readProductBounds } from "./studioFloorExtent";
import { createStudioShadowRefresh } from "./studioShadowRefresh";

/**
 * Layer the shadow camera renders. Enabled on every opaque product mesh by
 * {@link markCasters}, and on nothing else, so the floor grid, the two shadow
 * planes and the transparent helpers never cast.
 */
export const STUDIO_SHADOW_CASTER_LAYER = 2;

/** Where the shadow camera aims: mid-height of a typical case, on the floor's centre. */
const SHADOW_TARGET = new Vector3(0, 0.4, 0);

/**
 * Height above the floor that maps to 1 in the mask's height channel, in
 * metres. Anything taller casts as if it were this tall; a case rarely is.
 */
const HEIGHT_RANGE_M = 1.5;

/**
 * Samples to each side in a blur pass, at {@link BLUR_STRIDE} texels apart.
 * Together they set the widest penumbra the blur can produce, in texels.
 */
const BLUR_TAPS = 36;
const BLUR_STRIDE = 2;

/**
 * Silhouette of whatever the shadow camera sees: red is coverage, green the
 * caster's height above the floor as a fraction of {@link HEIGHT_RANGE_M}.
 * Depth is irrelevant; the height is what the blur widens by.
 *
 * World height is taken after the instance transform, so instanced hardware
 * (rivets, corner fittings) reports its real height and not its prototype's.
 */
function createSilhouetteMaterial(): MeshDepthMaterial {
  const material = new MeshDepthMaterial();
  material.onBeforeCompile = (shader) => {
    shader.uniforms.uHeightScale = { value: 1 / HEIGHT_RANGE_M };
    shader.vertexShader = shader.vertexShader
      .replace("void main() {", "varying float vShadowHeight;\nvoid main() {")
      .replace(
        "#include <project_vertex>",
        /* glsl */ `
        #include <project_vertex>
        vec4 shadowWorld = vec4( transformed, 1.0 );
        #ifdef USE_INSTANCING
          shadowWorld = instanceMatrix * shadowWorld;
        #endif
        vShadowHeight = ( modelMatrix * shadowWorld ).y;
        `,
      );
    shader.fragmentShader = shader.fragmentShader
      .replace(
        "void main() {",
        "uniform float uHeightScale;\nvarying float vShadowHeight;\nvoid main() {",
      )
      .replace(
        "vec4( vec3( 1.0 - fragCoordZ ), opacity );",
        "vec4( 1.0, clamp( vShadowHeight * uHeightScale, 0.0, 1.0 ), 0.0, 1.0 );",
      );
  };
  return material;
}

interface BlurUniforms extends Record<string, IUniform> {
  tMask: IUniform<Texture | null>;
  /** One texel step along the pass's axis, in UV. */
  direction: IUniform<Vector2>;
  /** Blur radius at floor level and at {@link HEIGHT_RANGE_M}, in texels. */
  minRadius: IUniform<number>;
  maxRadius: IUniform<number>;
}

/**
 * One axis of a separable blur whose radius is the *source* texel's, not the
 * destination's.
 *
 * A gather blur reads the radius at the pixel being written, which is wrong
 * here: outside the silhouette the height is zero, so the lid's shadow would
 * never spread past its own edge. Instead every source texel scatters its
 * coverage over a kernel of its own radius, and the loop below is that
 * scatter written as a gather: a source at distance `d` contributes if `d` is
 * inside *its* radius, with the kernel normalised to integrate to one over
 * that radius so a solid interior still sums to one. The height carried on to
 * the next pass is the tallest source that contributed, which keeps the
 * second axis as wide as the first.
 *
 * Cost is `2 * BLUR_TAPS + 1` reads per texel of the mask, twice, on the
 * frames the shadow re-renders.
 */
function createBlurMaterial(): {
  material: ShaderMaterial;
  uniforms: BlurUniforms;
} {
  const uniforms: BlurUniforms = {
    tMask: { value: null },
    direction: { value: new Vector2() },
    minRadius: { value: 1 },
    maxRadius: { value: 1 },
  };
  const material = new ShaderMaterial({
    uniforms,
    defines: { TAPS: BLUR_TAPS, STRIDE: BLUR_STRIDE.toFixed(1) },
    vertexShader: /* glsl */ `
      varying vec2 vUv;
      void main() {
        vUv = uv;
        gl_Position = vec4( position.xy, 0.0, 1.0 );
      }
    `,
    fragmentShader: /* glsl */ `
      uniform sampler2D tMask;
      uniform vec2 direction;
      uniform float minRadius;
      uniform float maxRadius;
      varying vec2 vUv;
      void main() {
        float coverage = 0.0;
        float height = 0.0;
        for ( int i = -TAPS; i <= TAPS; i++ ) {
          float d = float( i ) * STRIDE;
          vec2 source = texture2D( tMask, vUv + direction * d ).rg;
          if ( source.r <= 0.0 ) continue;
          float radius = mix( minRadius, maxRadius, source.g );
          float x = abs( d ) / radius;
          if ( x >= 1.0 ) continue;
          // 1 - smoothstep, which integrates to radius / 2 per side.
          float kernel = 1.0 - x * x * ( 3.0 - 2.0 * x );
          coverage += source.r * kernel * STRIDE / radius;
          height = max( height, source.g );
        }
        gl_FragColor = vec4( min( coverage, 1.0 ), height, 0.0, 1.0 );
      }
    `,
    depthTest: false,
    depthWrite: false,
  });
  return { material, uniforms };
}

interface ReceiverUniforms extends Record<string, IUniform> {
  map: IUniform<Texture>;
  lightMatrix: IUniform<Matrix4>;
  opacity: IUniform<number>;
}

/**
 * Floor plane that darkens by the blurred silhouette, looked up through the
 * shadow camera's view-projection — a shadow map without the depth compare,
 * because the plane is the only receiver and everything above it casts.
 */
function createReceiverMaterial(uniforms: ReceiverUniforms): ShaderMaterial {
  return new ShaderMaterial({
    uniforms,
    vertexShader: /* glsl */ `
      uniform mat4 lightMatrix;
      varying vec4 vLight;
      void main() {
        vec4 worldPosition = modelMatrix * vec4( position, 1.0 );
        vLight = lightMatrix * worldPosition;
        gl_Position = projectionMatrix * viewMatrix * worldPosition;
      }
    `,
    fragmentShader: /* glsl */ `
      uniform sampler2D map;
      uniform float opacity;
      varying vec4 vLight;
      void main() {
        vec2 uv = vLight.xy / vLight.w * 0.5 + 0.5;
        vec2 inside = step( vec2( 0.0 ), uv ) * step( uv, vec2( 1.0 ) );
        float shadow = texture2D( map, uv ).r * inside.x * inside.y;
        gl_FragColor = vec4( 0.0, 0.0, 0.0, shadow * opacity );
      }
    `,
    transparent: true,
    depthWrite: false,
  });
}

function isOpaque(material: Material | Material[]): boolean {
  return Array.isArray(material)
    ? material.every((entry) => !entry.transparent)
    : !material.transparent;
}

/**
 * Puts every opaque mesh on the caster layer and takes everything else off
 * it. Transparency is the discriminator on purpose: the floor grid, the
 * contact-shadow pool, this component's own plane and the loading
 * placeholders are all transparent, and the product is not.
 */
function markCasters(root: Object3D): void {
  root.traverse((object) => {
    if (!(object as Mesh).isMesh) return;
    const mesh = object as Mesh;
    // The stand-in caster owns SHADOW_PROXY_LAYER and must stay off this one,
    // or a settled capture would draw the product and its box together.
    if (isShadowProxy(mesh)) return;
    const casts =
      isOpaque(mesh.material) && !mesh.layers.isEnabled(AO_EXCLUDED_LAYER);
    if (casts) mesh.layers.enable(STUDIO_SHADOW_CASTER_LAYER);
    else mesh.layers.disable(STUDIO_SHADOW_CASTER_LAYER);
  });
}

interface StudioShadowProps {
  /** Height of the receiving plane, in metres. */
  y: number;
  /** Darkest the shadow gets, where the silhouette is solid. */
  opacity?: number;
  /**
   * Half-width of the penumbra per metre of caster height. A softbox of
   * angular size θ casts a penumbra `height * θ` wide, so this is θ / 2: the
   * key is 2.4 m across at 4 m, about 0.6 rad.
   */
  penumbraPerMetre?: number;
  /** Half-width of the penumbra at floor level, in metres, so contact is not razor-edged. */
  contactPenumbraM?: number;
  /** Half-width of the shadow camera's frustum, in metres. */
  extent?: number;
  /** Edge length of the silhouette mask, in texels. */
  resolution?: number;
  /**
   * Minimum milliseconds between changed shadow captures. The first capture
   * is immediate; a pending change wakes one final frame at the deadline.
   * Defaults to 0 (unthrottled); invalid/negative values also mean 0.
   */
  updateIntervalMs?: number;
  /**
   * The same while the user controls the camera. Defaults to 0, so the
   * shadow turns with a user orbit on every frame instead of in steps.
   */
  controlledUpdateIntervalMs?: number;
}

/**
 * Soft directional shadow on the floor, cast from the studio's key softbox.
 *
 * Image-based lighting cannot cast a shadow, and the contact pool under the
 * case is the same from every side; without this nothing in the image says
 * where the light is. This does, and it agrees with the highlights: it takes
 * its direction from {@link STUDIO_KEY} and turns with the camera exactly as
 * the studio does, so the case orbits through a fixed shadow the way a
 * product does on a turntable.
 *
 * It is a projected planar shadow, not a light with a shadow map. The product
 * is rendered once as a silhouette from the key's direction into a small mask,
 * the mask is blurred, and the floor plane looks the blurred mask up through
 * that camera's projection. No light enters the scene, so the HDRI stays the
 * only light source, no product material compiles shadow code, and nothing is
 * evaluated per pixel on the case. The cost is one depth-only render of the
 * product and two blur quads — and only on frames where the camera has
 * orbited or the product has changed; a still frame re-renders nothing.
 *
 * The penumbra widens with the caster's height, as a softbox's does: the
 * silhouette records how high each texel's caster is, and the blur spreads
 * each texel by a radius taken from that. The edge where the base meets the
 * floor stays crisp; the far edge, thrown by the lid, is wide and soft.
 */
export const StudioShadow = ({
  y,
  opacity = 0.55,
  penumbraPerMetre = 0.3,
  contactPenumbraM = 0.012,
  extent = 1.6,
  resolution = 512,
  updateIntervalMs = 0,
  controlledUpdateIntervalMs = 0,
}: StudioShadowProps) => {
  const gl = useThree((state) => state.gl);
  const scene = useThree((state) => state.scene);
  const invalidate = useThree((state) => state.invalidate);
  // Absent outside a product viewer; the camera invalidates on every change.
  const store = useContext(ProductStoreContext);
  const followCamera =
    useEnvironmentStudio((state) => state.followCamera) ?? true;

  const resources = useMemo(() => {
    const mask = new WebGLRenderTarget(resolution, resolution);
    const scratch = new WebGLRenderTarget(resolution, resolution, {
      depthBuffer: false,
    });
    mask.texture.generateMipmaps = scratch.texture.generateMipmaps = false;
    mask.texture.name = "ProductViewer.studioShadow";

    const camera = new OrthographicCamera(
      -extent,
      extent,
      extent,
      -extent,
      0.1,
      20,
    );
    camera.layers.set(STUDIO_SHADOW_CASTER_LAYER);

    const blurScene = new Scene();
    const blurCamera = new OrthographicCamera(-1, 1, 1, -1, 0, 1);
    const blur = createBlurMaterial();
    const quad = new Mesh(new PlaneGeometry(2, 2), blur.material);
    blurScene.add(quad);

    const receiverUniforms: ReceiverUniforms = {
      map: { value: mask.texture },
      lightMatrix: { value: new Matrix4() },
      opacity: { value: opacity },
    };
    const receiver = createReceiverMaterial(receiverUniforms);

    // Radii in texels of the mask.
    const metresPerTexel = (extent * 2) / resolution;
    blur.uniforms.minRadius.value = Math.max(
      1,
      contactPenumbraM / metresPerTexel,
    );
    blur.uniforms.maxRadius.value = Math.max(
      blur.uniforms.minRadius.value,
      (HEIGHT_RANGE_M * penumbraPerMetre) / metresPerTexel,
    );

    return {
      mask,
      scratch,
      camera,
      silhouette: createSilhouetteMaterial(),
      blurScene,
      blurCamera,
      blur,
      quad,
      receiver,
      receiverUniforms,
      geometry: new PlaneGeometry(extent * 2.5, extent * 2.5),
      direction: new Vector3(),
      forward: new Vector3(),
      clearColor: new Color(),
    };
  }, [resolution, extent, opacity, penumbraPerMetre, contactPenumbraM]);

  useEffect(
    () => () => {
      resources.mask.dispose();
      resources.scratch.dispose();
      resources.silhouette.dispose();
      resources.blur.material.dispose();
      resources.quad.geometry.dispose();
      resources.receiver.dispose();
      resources.geometry.dispose();
    },
    [resources],
  );

  // True while the caster layer has not been re-marked since the proxy last
  // stood in for the product.
  const castersStale = useRef(false);
  const refresh = useRef<ReturnType<typeof createStudioShadowRefresh> | null>(
    null,
  );
  useLayoutEffect(() => {
    // Each committed renderer/scene/resource context owns its wake. Creating
    // this in the effect also survives StrictMode's setup/cleanup replay.
    const scheduler = createStudioShadowRefresh({
      invalidate,
      updateIntervalMs,
      controlledUpdateIntervalMs,
      refresh: ({ yaw }, revisionChanged) => {
        const {
          mask,
          scratch,
          camera: shadowCamera,
          silhouette,
          blurScene,
          blurCamera,
          blur,
          receiverUniforms,
          direction,
          clearColor,
        } = resources;

        // A gesture recaptures this over and over, and the result is blurred
        // into a soft pool either way, so the product's own silhouette is only
        // worth its draw calls once the case has stopped moving.
        // Non-empty, not merely published: `publishProductBounds` copies into
        // a kept box, so an entry can be present and degenerate, and
        // `fitShadowProxy` refuses exactly that box — the camera would switch
        // to the proxy layer while the proxy kept its last transform.
        const bounds = readProductBounds(scene);
        const useProxy = shouldUseShadowProxy({
          dragActive: isSceneDragActive(scene),
          hasBounds: bounds !== null && !bounds.isEmpty(),
        });
        if (useProxy) {
          // Skipping the mark leaves the caster layer stale, so the first
          // settled capture after a gesture has to re-mark whatever the
          // revision did while the proxy was standing in.
          castersStale.current = true;
        } else if (revisionChanged || castersStale.current) {
          markCasters(scene);
          castersStale.current = false;
        }
        shadowCamera.layers.set(
          useProxy ? SHADOW_PROXY_LAYER : STUDIO_SHADOW_CASTER_LAYER,
        );
        studioKeyDirection(yaw, direction);
        shadowCamera.position.copy(SHADOW_TARGET).addScaledVector(direction, 8);
        shadowCamera.lookAt(SHADOW_TARGET);
        shadowCamera.updateMatrixWorld();

        const previousTarget = gl.getRenderTarget();
        const previousCubeFace = gl.getActiveCubeFace();
        const previousMipmapLevel = gl.getActiveMipmapLevel();
        const previousBackground = scene.background;
        const previousOverride = scene.overrideMaterial;
        const previousAutoClear = gl.autoClear;
        gl.getClearColor(clearColor);
        const previousClearAlpha = gl.getClearAlpha();

        try {
          scene.background = null;
          scene.overrideMaterial = silhouette;
          gl.setClearColor(0x000000, 0);
          gl.autoClear = true;
          gl.setRenderTarget(mask);
          gl.render(scene, shadowCamera);

          const texel = 1 / resolution;
          blur.uniforms.tMask.value = mask.texture;
          blur.uniforms.direction.value.set(texel, 0);
          gl.setRenderTarget(scratch);
          gl.render(blurScene, blurCamera);
          blur.uniforms.tMask.value = scratch.texture;
          blur.uniforms.direction.value.set(0, texel);
          gl.setRenderTarget(mask);
          gl.render(blurScene, blurCamera);
        } finally {
          scene.overrideMaterial = previousOverride;
          scene.background = previousBackground;
          gl.autoClear = previousAutoClear;
          gl.setClearColor(clearColor, previousClearAlpha);
          gl.setRenderTarget(
            previousTarget,
            previousCubeFace,
            previousMipmapLevel,
          );
        }

        receiverUniforms.lightMatrix.value.multiplyMatrices(
          shadowCamera.projectionMatrix,
          shadowCamera.matrixWorldInverse,
        );
      },
    });
    refresh.current = scheduler;
    // A context/interval switch may cancel the only pending wake. Request a
    // frame for the new scheduler instead of losing that final capture.
    invalidate();
    return () => {
      scheduler.dispose();
      refresh.current = null;
    };
  }, [
    gl,
    scene,
    resources,
    resolution,
    invalidate,
    updateIntervalMs,
    controlledUpdateIntervalMs,
  ]);

  // Before the composer's frame (priority 1). Always observe live inputs;
  // only the shadow capture is throttled, never the scene revision itself.
  useFrame(({ camera }) => {
    refresh.current?.update(
      getSceneRevision(scene),
      studioYaw(camera, followCamera, resources.forward),
      store?.getState().isUserControllingCamera ?? false,
    );
  });

  return (
    <mesh
      geometry={resources.geometry}
      material={resources.receiver}
      position={[0, y, 0]}
      rotation={[-Math.PI / 2, 0, 0]}
      // Last of the three floor layers — patch, pool, this — all transparent
      // and none writing depth, so order is what decides the blend.
      renderOrder={3}
    />
  );
};
